import React, { useState } from "react";
import { Stack, IconButton, Spinner, SpinnerSize } from "@fluentui/react";
import { DocumentRegular, ImageRegular, DismissRegular, ErrorCircleRegular, WarningRegular, ChevronDownRegular, ChevronUpRegular } from "@fluentui/react-icons";
import { ConversationAttachment } from "../../api";
import styles from "./AttachmentPills.module.css";

interface Props {
  attachments: ConversationAttachment[];
  onDelete: (attachmentId: string) => void;
  isUploading: boolean;
  uploadError?: { message: string; details?: string } | null;
  onDismissError?: () => void;
  uploadWarning?: string | null;
  onDismissWarning?: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageMimeType = (mimeType: string): boolean =>
  mimeType.startsWith("image/");

const truncateFileName = (name: string, maxLength: number = 25): string => {
  if (name.length <= maxLength) return name;
  const ext = name.lastIndexOf(".");
  if (ext > 0) {
    const extension = name.substring(ext);
    const baseName = name.substring(0, ext);
    const maxBase = maxLength - extension.length - 3;
    if (maxBase > 0) {
      return `${baseName.substring(0, maxBase)}...${extension}`;
    }
  }
  return `${name.substring(0, maxLength - 3)}...`;
};

export const AttachmentPills: React.FC<Props> = ({ attachments, onDelete, isUploading, uploadError, onDismissError, uploadWarning, onDismissWarning }) => {
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  if (attachments.length === 0 && !isUploading && !uploadError && !uploadWarning) return null;

  return (
    <Stack horizontal wrap tokens={{ childrenGap: 6 }} className={styles.pillsContainer}>
      {uploadWarning && (
        <div className={styles.warningPill} role="status">
          <span className={styles.warningIcon}><WarningRegular /></span>
          <span className={styles.warningText}>{uploadWarning}</span>
          {onDismissWarning && (
            <button
              className={styles.warningDismiss}
              onClick={onDismissWarning}
              aria-label="Dismiss warning"
              title="Dismiss"
            >
              <DismissRegular style={{ fontSize: 12 }} />
            </button>
          )}
        </div>
      )}
      {uploadError && (
        <div className={styles.errorPill} role="alert">
          <div className={styles.errorHeader}>
            <span className={styles.errorIcon}><ErrorCircleRegular /></span>
            <span className={styles.errorText}>{uploadError.message}</span>
            {uploadError.details && (
              <button
                className={styles.errorToggle}
                onClick={() => setDetailsExpanded(prev => !prev)}
                aria-label={detailsExpanded ? "Hide details" : "Show details"}
                title={detailsExpanded ? "Hide details" : "Show details"}
              >
                {detailsExpanded ? <ChevronUpRegular style={{ fontSize: 12 }} /> : <ChevronDownRegular style={{ fontSize: 12 }} />}
              </button>
            )}
            {onDismissError && (
              <button
                className={styles.errorDismiss}
                onClick={onDismissError}
                aria-label="Dismiss error"
                title="Dismiss"
              >
                <DismissRegular style={{ fontSize: 12 }} />
              </button>
            )}
          </div>
          {uploadError.details && detailsExpanded && (
            <div className={styles.errorDetails}>{uploadError.details}</div>
          )}
        </div>
      )}
      {attachments.map((attachment) => (
        <div key={attachment.id} className={styles.pill} title={attachment.file_name}>
          <span className={styles.pillIcon}>
            {isImageMimeType(attachment.mime_type) ? (
              <ImageRegular />
            ) : (
              <DocumentRegular />
            )}
          </span>
          <span className={styles.pillName}>
            {truncateFileName(attachment.file_name)}
          </span>
          <span className={styles.pillSize}>
            {formatFileSize(attachment.file_size)}
          </span>
          {attachment.was_summarized && (
            <span className={styles.summarizedBadge} title="This document was summarized due to its size">
              summarized
            </span>
          )}
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            className={styles.dismissButton}
            onClick={() => onDelete(attachment.id)}
            ariaLabel={`Remove ${attachment.file_name}`}
            title="Remove file"
          />
        </div>
      ))}
      {isUploading && (
        <div className={styles.pill}>
          <Spinner size={SpinnerSize.xSmall} />
          <span className={styles.pillName}>Uploading...</span>
        </div>
      )}
    </Stack>
  );
};
